import { Controller } from "@hotwired/stimulus"

// How long an auto-advance step will wait for its own save before moving on
// anyway. A save on a healthy connection lands well inside this, so the common
// path is unchanged; the cap only matters when the network is slow enough that
// waiting would strand the user.
const ADVANCE_WAIT_MS = 600

// Ceiling on a single step submission. Submissions share one serial queue, so
// a request that never settles would otherwise block every later step's save
// for the rest of the session.
const SUBMIT_TIMEOUT_MS = 10000
// The skin_type_quiz_v2 field test: control keeps the three-question quiz.
const V1_QUIZ_STEP_KEYS = ["skin_type_quiz_1", "skin_type_quiz_2", "skin_type_quiz_3"]

export default class extends Controller {
    static targets = ["step", "progressBar", "continueButton", "secondaryButton", "signInLink", "progressContainer", "stepForm", "backButton", "skipButton", "buttonContainer", "scrollContainer", "nativeReviewPromptContent", "profileSaveStatus", "profileSaveMessage", "profileSaveRetry", "flow", "skinTypeResultFrame", "resultSaveStatus", "resultSaveMessage", "resultSaveRetry"]
    static values = {
        current: Number,
        total: Number,
        onboardingFour: { type: Boolean, default: false },
        // onboarding_5 is a delta on the control flow, so it never turns on the
        // onboarding_4-only behaviours (the skin type fork, the wasted spend
        // and held back echoes, the plan reveal). It only changes the routine
        // tail and the goal-led screens it shares with onboarding_4.
        onboardingFive: { type: Boolean, default: false },
        skinTypeQuizV2: { type: Boolean, default: true },
        profileCompleted: { type: Boolean, default: false },
        afterProfileGeneration: { type: String, default: "plan_reveal" },
        formCompleted: { type: Boolean, default: false },
        currentKey: String,
        allowBack: { type: Boolean, default: false },
        allowSkip: { type: Boolean, default: false },
        // Add new values for skin type quiz
        // Track if native review prompt has been shown
        nativeReviewPromptShown: { type: Boolean, default: false },
        // Track if product search has been tapped
        productSearchTapped: { type: Boolean, default: false },
        // Whether the onboarding routine contains at least one product
        routineHasProducts: { type: Boolean, default: false },
        // Referral attribution branch state — seeded from the server based on
        // the user's saved onboarding data, then updated as they make picks.
        referralSource: { type: String, default: '' },
        referralSourceDetail: { type: String, default: '' },
        referralNextStep: { type: String, default: 'product_preferences' },
        referralDetailTriggerSources: { type: Array, default: [] },
        skinGoal: { type: String, default: '' },
        // Server-seeded answers used as fallbacks by the answer-echo screens
        // when the source question's radios aren't checked (e.g. a reload
        // later in the flow re-renders radios unchecked).
        wastedSpend: { type: String, default: '' },
        heldBack: { type: String, default: '' },
        // The obstacle the user picked on the struggle question
        // (user_jobs_to_be_done). onboarding_5 keys the timeline preview's
        // headline off it, so it is kept current as the answer changes for the
        // same reason skinGoal is.
        obstacle: { type: String, default: '' },
        // Diagnosed-condition keys seeded from the user's saved onboarding
        // data, used as a fallback when the checkbox form isn't available.
        diagnosedConditions: { type: Array, default: [] },
        usedKnownSkinType: { type: Boolean, default: false },
        // The skin type saved on the user, used as the fallback for the same
        // reason as the answers above: the picker's radios come back unchecked
        // after a reload later in the flow.
        skinType: { type: String, default: '' },
        // True when the user already has a premium subscription. Premium users
        // shouldn't be routed to the paywall at the end of onboarding — they
        // exit the flow cleanly instead.
        userPremium: { type: Boolean, default: false },
        // Where a premium web user goes when they finish. Root, unless they
        // bought from the signup paywall and came from a page worth returning to.
        premiumExitPath: { type: String, default: '/' },
        showAuthButton: { type: Boolean, default: false }
    }

    // Steps that require animation completion before showing continue button
    animationControlledSteps = ["skin_profile_setup", "skin_profile_generation"]

    // When several conditions are selected, the affirmation leads with the one
    // the match score treats most severely (hard caps first).
    DIAGNOSED_CONDITION_PRIORITY = [
        "fungal_acne",
        "eczema",
        "seborrheic_dermatitis",
        "rosacea",
        "perioral_dermatitis",
        "psoriasis",
        "keratosis_pilaris"
    ]

    // Steps that should always show the continue button (always considered "completed")
    alwaysCompletedSteps = ["routine_information", "no_routine_affirmation", "product_preferences", "custom_avoided_ingredients", "region_preference", "diagnosed_conditions"]

    // Region multi-select rules: "anywhere" is exclusive and at most 3 regions
    // may be picked at once.
    MAX_REGION_SELECTIONS = 3

    // Give the native review sheet time to appear before onboarding can move on.
    NATIVE_REVIEW_CONTINUE_DELAY_MS = 2000

    // Track which steps have already been tracked to prevent duplicate events
    trackedSteps = {}

    // Same, keyed by paywall placement. A user can tap the continue button more
    // than once before the navigation commits.
    trackedPaywalls = {}

    // Client-side mirror of the user's skin goal follow-up answers, keyed by
    // question. Used to derive whether the secondary concern confirm step is
    // relevant. The server (StoreOnboardingDataOnUser) re-derives the same thing
    // from persisted answers before storing anything.
    followUpAnswers = {}

    // Panel key ("goal:question:secondary") of the matched secondary concern
    // rule, or null when no rule applies for the current answers.
    activeSecondaryPanelKey = null

    // Keep answer writes ordered so rapid multi-select changes cannot arrive
    // out of sequence and overwrite the user's final selection.
    submissionQueue = Promise.resolve()
    pendingAnswers = new Map()

    connect() {
        this.disconnected = false
        // Store bound handler references once so disconnect() can remove the
        // exact same function objects. Binding again in removeEventListener
        // would create new objects that never match, leaking listeners across
        // every Turbo/native navigation.
        this.boundHandlePopState = this.handlePopState.bind(this)
        this.boundHandleAnimationsComplete = this.handleAnimationsComplete.bind(this)
        this.boundHandleProductSearchTapped = this.handleProductSearchTapped.bind(this)
        this.boundHandleProductAdded = this.handleProductAdded.bind(this)
        this.boundHandleContinueButtonClick = this.handleContinueButtonClick.bind(this)

        // Add popstate event listener for browser back/forward buttons
        window.addEventListener('popstate', this.boundHandlePopState)

        // Records the end-of-onboarding paywall, which is a plain href on this
        // button rather than a method call.
        if (this.hasContinueButtonTarget) {
            this.continueButtonTarget.addEventListener('click', this.boundHandleContinueButtonClick)
        }

        // Add listener for animation complete event for all animated steps
        document.addEventListener('onboarding:animations-complete', this.boundHandleAnimationsComplete)

        // Add listener for product search tapped event
        document.addEventListener('onboarding:product-search-tapped', this.boundHandleProductSearchTapped)
        document.addEventListener('onboarding:product-added', this.boundHandleProductAdded)

        // Initialize with a slight delay to ensure all is loaded
        setTimeout(() => {
            this.initializeSteps()

            // Additional check specifically for the last step
            if (this.currentValue === this.stepTargets.length - 1) {
                console.log("Connected on last step, updating continue button")
                this.updateContinueButton()
            }
        }, 100)
    }

    initializeSteps() {
        try {
            // Check if we have step targets before proceeding
            if (!this.hasStepTarget || this.stepTargets.length === 0) {
                console.error("No step targets found")
                return
            }

            // Safety check - make sure currentValue is within range
            if (this.currentValue === undefined || this.currentValue < 0) {
                this.currentValue = 0
            } else if (this.currentValue >= this.stepTargets.length) {
                this.currentValue = this.stepTargets.length - 1
            }

            this.syncSkinGoalFollowUps()
            this.seedFollowUpAnswers()
            this.syncSecondaryConcernConfirm()

            if (!this.isRelevantStep(this.stepTargets[this.currentValue])) {
                const skinGoalsIndex = this.stepTargets.findIndex(step => step.dataset.stepKey === 'skin_goals')
                if (skinGoalsIndex !== -1) this.currentValue = skinGoalsIndex
            }

            this.currentValue = Math.max(this.currentValue, this.profileNavigationFloor)

            // Get the current step
            const currentStep = this.stepTargets[this.currentValue]

            // Set form completed based on step type
            if (currentStep && currentStep.dataset.stepType === "education") {
                this.formCompletedValue = true
            } else if (currentStep && (currentStep.dataset.answerOptional === "true" ||
                !this.onboardingFourValue && this.alwaysCompletedSteps.includes(currentStep.dataset.stepKey))) {
                // Always show continue button for these steps
                this.formCompletedValue = true
            } else if (currentStep && currentStep.dataset.stepKey === "routine_add_products") {
                // v5 lets beginners continue without opening search or adding products.
                this.formCompletedValue = this.onboardingFiveValue || this.productSearchTappedValue || currentStep.dataset.hasRoutineProducts === "true"
            } else {
                this.formCompletedValue = this.stepFormComplete(currentStep)
            }
            if (currentStep?.dataset.stepKey === "skin_type_result") this.formCompletedValue = false

            // Update navigation controls based on step settings
            this.updateNavigationControls()

            // Update the UI
            this.showCurrentStep()
            this.updateProgressBar()
            this.updateContinueButton()
            this.updateSecondaryButton()

        } catch (error) {
            console.error("Error initializing onboarding steps:", error)
        }
    }

    updateNavigationControls() {
        try {
            const currentStep = this.stepTargets[this.currentValue]
            if (!currentStep) return

            // Update navigation control values based on step data attributes
            this.allowBackValue = currentStep.dataset.allowBack === "true" && this.currentValue > this.profileNavigationFloor
            this.allowSkipValue = currentStep.dataset.allowSkip === "true"

            // Update button visibility
            if (this.hasBackButtonTarget) {
                if (this.allowBackValue) {
                    this.backButtonTarget.classList.remove("hidden")
                } else {
                    this.backButtonTarget.classList.add("hidden")
                }
            }

            if (this.hasSkipButtonTarget) {
                if (this.allowSkipValue) {
                    this.skipButtonTarget.classList.remove("hidden")
                } else {
                    this.skipButtonTarget.classList.add("hidden")
                }
            }
        } catch (error) {
            console.error("Error updating navigation controls:", error)
        }
    }

    disconnect() {
        this.disconnected = true
        this.cancelAutoAdvance()
        this.profileSaveAttempt = null
        // Remove event listeners using the same bound references added in
        // connect() so they are actually detached.
        window.removeEventListener('popstate', this.boundHandlePopState)
        document.removeEventListener('onboarding:animations-complete', this.boundHandleAnimationsComplete)
        document.removeEventListener('onboarding:product-search-tapped', this.boundHandleProductSearchTapped)
        document.removeEventListener('onboarding:product-added', this.boundHandleProductAdded)

        if (this.hasContinueButtonTarget) {
            this.continueButtonTarget.removeEventListener('click', this.boundHandleContinueButtonClick)
        }

        if (this.nativeReviewContinueTimer) {
            clearTimeout(this.nativeReviewContinueTimer)
        }
    }

    handlePopState(event) {
        try {
            // Extract step key from URL path
            const pathParts = window.location.pathname.split('/')
            const stepKey = pathParts[pathParts.length - 1]

            // Find step index by key
            let stepIndex = this.stepTargets.findIndex(step => step.dataset.stepKey === stepKey)

            if (stepIndex !== -1 && stepIndex < this.profileNavigationFloor) {
                stepIndex = this.profileNavigationFloor
                const path = `/onboarding/${this.stepTargets[stepIndex].dataset.stepKey}`
                window.history.replaceState({ path }, '', path)
            }

            if (stepIndex !== -1 && stepIndex !== this.currentValue) {
                this.currentValue = stepIndex
                this.transition(false) // Don't update browser history on popstate
            }
        } catch (error) {
            console.error("Error handling popstate:", error)
        }
    }

    showCurrentStep() {
        this.cancelAutoAdvance()
        this.profileSaveAttempt = null
        if (this.hasProfileSaveStatusTarget) this.profileSaveStatusTarget.classList.add("hidden")
        if (this.hasFlowTarget) this.flowTarget.inert = false

        // Completion analytics closes profile writes on the server. Do not
        // show generation (or start its animation) while answers are unsaved.
        if (this.stepTargets[this.currentValue]?.dataset.stepKey === "skin_profile_generation" &&
            this.pendingAnswers.size > 0) {
            this.saveProfileAnswers()
            return
        }

        try {
            // First, hide all other steps immediately
            const currentStepElement = this.stepTargets[this.currentValue];
            const currentKey = currentStepElement?.dataset.stepKey;

            if (this.hasSkinTypeResultFrameTarget && currentKey !== "skin_type_result") {
                this.resultLoadAttempt = null
                this.skinTypeResultFrameTarget.removeAttribute("src")
                this.skinTypeResultFrameTarget.innerHTML = '<p role="status" class="py-12 text-warm-gray-600">Reading your skin profile...</p>'
            }

            // The analytics event for this screen closes profile editing on
            // the server. Mirror that boundary before navigation is possible.
            if (this.onboardingFiveValue && currentKey === "skin_profile_generation") {
                this.profileCompletedValue = true
                this.updateNavigationControls()
            }

            if (this.shouldPresentSignupPaywallBeforeStep(currentKey)) {
                this.presentSignupPaywall()
                return
            }

            // Guarded: a native WebView can run this controller against a cached
            // DOM that predates the target, and an unguarded read throws inside
            // this try/catch, silently skipping the show/hide pass below.
            if (this.hasScrollContainerTarget) this.scrollContainerTarget.scrollTop = 0

            // Immediately set all steps to opacity 0 and hidden (except current)
            this.stepTargets.forEach((step, index) => {
                if (index !== this.currentValue) {
                    step.style.opacity = "0";
                    step.classList.add("hidden");
                }
            });

            // Now show the current step with a clean transition
            if (currentStepElement) {
                // Make sure the step is hidden but in the DOM
                currentStepElement.style.opacity = "0";
                currentStepElement.classList.remove("hidden");

                this.triggerNativeReviewPromptOnStepEntry(currentStepElement)

                // Special handling for animation-controlled steps
                if (this.animationControlledSteps.includes(currentKey)) {
                    this.formCompletedValue = false;
                    this.updateContinueButton();
                }

                // Give the browser a moment to process before fading in
                setTimeout(() => {
                    if (this.disconnected || this.stepTargets[this.currentValue] !== currentStepElement) return

                    currentStepElement.style.opacity = "1";

                    // Dispatch a custom event when the step is fully shown
                    const stepShownEvent = new CustomEvent('onboarding:step-shown', {
                        bubbles: true,
                        detail: { stepKey: currentKey }
                    });
                    window.dispatchEvent(stepShownEvent);

                    // Track this step via analytics if we haven't already
                    this.trackStepView(currentKey);

                    // Sync the platform-specific copy/icon on the referral
                    // attribution branch each time the step is shown — the
                    // markup is rendered server-side once, but the user can
                    // change their referral source via back-nav.
                    if (currentKey === 'referral_source_detail') {
                        this.syncReferralSourceDetailUi();
                    } else if (currentKey === 'skin_type_result') {
                        this.loadSkinTypeResultCard();
                    } else if (currentKey === 'skin_goals_affirmation') {
                        this.syncSkinGoalAffirmation();
                    } else if (currentKey === 'sun_exposure_affirmation') {
                        this.syncSunExposureAffirmation();
                    } else if (currentKey === 'diagnosed_conditions_affirmation') {
                        this.syncDiagnosedConditionsAffirmation();
                    } else if (currentKey === 'condition_screen_result') {
                        this.syncConditionScreenResult();
                    } else if (currentKey === 'profile_value_receipt') {
                        this.syncProfileValueReceipt();
                    } else if (currentKey === 'wasted_spend_reality') {
                        this.syncWastedSpendReality();
                    } else if (currentKey === 'goal_timeline_preview') {
                        this.syncGoalTimelinePreview();
                    } else if (currentKey === 'user_jobs_to_be_done_v2') {
                        this.syncSkincareStruggle();
                    } else if (currentKey === 'notification_permission') {
                        this.syncNotificationPermission();
                    } else if (currentKey === 'held_back_affirmation') {
                        this.syncHeldBackAffirmation();
                    } else if (currentKey === 'skin_profile_generation') {
                        this.syncSkinProfileGeneration();
                    } else if (currentKey === 'commitment') {
                        this.syncCommitment();
                    } else if (this.isSkinGoalFollowUp(currentStepElement)) {
                        this.syncSkinGoalFollowUps();
                    } else if (this.isSecondaryConcernConfirm(currentStepElement)) {
                        this.syncSecondaryConcernConfirm();
                    }
                }, 20);
            }

            // Update current key
            if (this.stepTargets[this.currentValue]) {
                this.currentKeyValue = currentKey
            }

            this.updateProgressContainer()

            // Always update the continue button when showing a step
            this.updateContinueButton()

            // Track event via the data layer
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: "onboarding_step_view",
                    step_key: this.currentKeyValue
                })
            }

            // Update browser history without reloading the page
            const newPath = `/onboarding/${this.currentKeyValue}`
            if (window.history && window.history.pushState && window.location.pathname !== newPath) {
                window.history.pushState({ path: newPath }, '', newPath)
            }
        } catch (error) {
            console.error("Error showing current step:", error)
        }
    }

    updateProgressBar() {
        try {
            if (this.hasProgressBarTarget) {
                const relevantSteps = this.relevantStepTargets()
                const relevantIndex = relevantSteps.indexOf(this.stepTargets[this.currentValue])
                const progress = ((relevantIndex + 1) / relevantSteps.length) * 100
                this.progressBarTarget.style.width = `${progress}%`
            }
        } catch (error) {
            console.error("Error updating progress bar:", error)
        }
    }

    updateProgressContainer() {
        if (!this.hasProgressContainerTarget) return

        const currentStep = this.stepTargets[this.currentValue]

        if (!currentStep || this.hideProgressForStep(currentStep)) {
            this.progressContainerTarget.classList.add("hidden")
        } else {
            this.progressContainerTarget.classList.remove("hidden")
        }
    }

    hideProgressForStep(step) {
        return step?.dataset.hideProgress === "true"
    }

    previous(event) {
        if (event) event.preventDefault()

        try {
            // Only allow going back if explicitly enabled for this step
            if (!this.allowBackValue) return

            // The product screen is reached from the routine introduction.
            const currentStep = this.stepTargets[this.currentValue]

            // onboarding_5 has no routine introduction: the product screen is
            // reached straight from the screening receipt, so back navigation
            // uses the previous relevant step.
            if (currentStep.dataset.stepKey === "routine_add_products" && !this.onboardingFiveValue) {
                const routineStep1Index = this.stepTargets.findIndex(step =>
                    step.dataset.stepKey === "routine_step_1" || step.dataset.stepKey === "routine_step_1_v2"
                )
                if (routineStep1Index !== -1) {
                    this.currentValue = routineStep1Index
                    this.transition()
                }
                return
            }

            // Mirror the conditional forward navigation for the referral
            // attribution branch, otherwise back nav lands on steps the user
            // legitimately skipped.
            if (currentStep.dataset.stepKey === this.referralNextStepValue && this.hasReferralAttributionSteps()) {
                this.navigateBackFromAfterReferralBranch()
                return
            }

            if (currentStep.dataset.stepKey === "skin_type_known_reactivity") {
                this.advanceToStep("skin_type_known")
                return
            }

            if (currentStep.dataset.stepKey === "skin_type_known_triggers") {
                this.advanceToStep("skin_type_known_reactivity")
                return
            }

            // In onboarding_5 the result card is also reached from the picker,
            // which sits before the quiz steps in the rendered order, so back
            // nav has to return to the picker rather than to quiz question 3.
            if (currentStep.dataset.stepKey === "skin_type_result" &&
                this.onboardingFiveValue && this.usedKnownSkinTypeValue) {
                this.advanceToStep(this.lastKnownSkinTypeStepKey())
                return
            }

            if (currentStep.dataset.stepKey === "post_skin_goal_follow_up_1" &&
                this.usedKnownSkinTypeValue && !this.onboardingFourValue) {
                this.advanceToStep(this.lastKnownSkinTypeStepKey())
                return
            }

            // Quiz-takers reach the first quiz step from the chooser that sits
            // before skin_type_known (skin_type_known_fork in onboarding_4, the
            // skin_type_quiz intro elsewhere). Mirror that skip on the way back
            // so they land on the chooser rather than skin_type_known, where
            // Continue would leave the skin type quiz behind and drop their
            // in-progress answers.
            if (currentStep.dataset.stepKey === this.firstQuizStepKey()) {
                this.advanceToStep(this.onboardingFourValue ? "skin_type_known_fork" : "skin_type_quiz")
                return
            }

            if (currentStep.dataset.stepKey === "influencer_search") {
                this.advanceToStep("referral_source_detail")
                return
            }

            if (currentStep.dataset.stepKey === "referral_source_detail") {
                this.advanceToStep("where_did_you_hear_about_us")
                return
            }

            if (currentStep.dataset.stepKey === "diagnosed_conditions" &&
                this.onboardingFourValue && this.usedKnownSkinTypeValue) {
                this.advanceToStep(this.lastKnownSkinTypeStepKey())
                return
            }

            const previousIndex = this.nextRelevantStepIndex(this.currentValue, -1)
            if (previousIndex !== -1) {
                this.currentValue = previousIndex
                this.transition()
            }
        } catch (error) {
            console.error("Error in previous method:", error)
        }
    }

    next(event) {
        if (event) event.preventDefault()

        try {
            if (this.stepTargets[this.currentValue]?.dataset.stepKey === "skin_profile_generation" &&
                this.pendingAnswers.size > 0) return

            // Don't proceed if form isn't completed for question type and skip isn't allowed
            const currentStep = this.stepTargets[this.currentValue]
            if (currentStep.dataset.stepType === "question" && !this.formCompletedValue && !this.allowSkipValue) {
                return
            }

            if ((currentStep.dataset.stepKey.startsWith("skin_type_quiz_v2_") ||
                 V1_QUIZ_STEP_KEYS.includes(currentStep.dataset.stepKey) ||
                 ["skin_type_known_reactivity", "skin_type_known_triggers", "skin_type_result"].includes(currentStep.dataset.stepKey)) &&
                !this.formCompletedValue) return

            // Special case for skin_type_quiz - navigate to the first quiz step
            if (currentStep.dataset.stepKey === "skin_type_quiz") {
                this.navigateToSkinTypeQuiz(event)
                return
            }

            if (currentStep.dataset.stepKey === "skin_type_known_fork") {
                this.navigateFromSkinTypeKnownFork(event)
                return
            }

            // The picker asks about sensitivity next, since sensitivity is no
            // longer a pickable skin type. The quiz test's control arm asks
            // nothing more, as the picker always has.
            if (currentStep.dataset.stepKey === "skin_type_known") {
                // Reaffirm the active branch even when a previously selected radio
                // has not changed after returning from the other branch.
                this.submitForm(currentStep.querySelector("form"))
                if (this.skinTypeQuizV2Value) {
                    this.advanceToStep("skin_type_known_reactivity")
                } else {
                    this.navigateFromKnownSkinTypeBranch(event)
                }
                return
            }

            if (currentStep.dataset.stepKey === "skin_type_known_reactivity") {
                this.submitForm(currentStep.querySelector("form"))
                if (this.lastKnownSkinTypeStepKey() === "skin_type_known_triggers") {
                    this.advanceToStep("skin_type_known_triggers")
                } else {
                    this.navigateFromKnownSkinTypeBranch(event)
                }
                return
            }

            if (currentStep.dataset.stepKey === "skin_type_known_triggers") {
                this.submitForm(currentStep.querySelector("form"))
                this.navigateFromKnownSkinTypeBranch(event)
                return
            }

            // Special case for routine_step_1 - check routine_status and route accordingly
            if (currentStep.dataset.stepKey === "routine_step_1" || currentStep.dataset.stepKey === "routine_step_1_v2") {
                this.navigateFromRoutineStep1(event)
                return
            }

            if (currentStep.dataset.stepKey === "no_routine_affirmation") {
                this.navigateFromNoRoutineAffirmation(event)
                return
            }

            if (currentStep.dataset.stepKey === "skin_profile_generation" && this.onboardingFourValue) {
                this.advanceToStep(this.afterProfileGenerationValue)
                return
            }

            if (currentStep.dataset.stepKey === "where_did_you_hear_about_us") {
                this.navigateFromReferralSource(event)
                return
            }

            if (currentStep.dataset.stepKey === "referral_source_detail") {
                this.navigateFromReferralSourceDetail(event)
                return
            }

            const nextIndex = this.nextRelevantStepIndex(this.currentValue, 1)
            if (nextIndex !== -1) {
                this.currentValue = nextIndex
                this.transition()
            }
        } catch (error) {
            console.error("Error in next method:", error)
        }
    }

    transition(updateHistory = true) {
        try {
            this.currentValue = Math.max(this.currentValue, this.profileNavigationFloor)

            // Update navigation controls for the new step
            this.updateNavigationControls()

            // Update progress
            this.updateProgressBar()

            // Set the new step's completion state before rendering it so the
            // continue button never inherits the previous step's enabled state.
            const currentStep = this.stepTargets[this.currentValue]
            const currentKey = currentStep?.dataset.stepKey;

            // Handle education steps vs. animation steps vs. question steps vs. always completed steps
            if (currentStep.dataset.stepType === "education" && !this.animationControlledSteps.includes(currentKey)) {
                this.formCompletedValue = true
            } else if (currentStep.dataset.answerOptional === "true" ||
                !this.onboardingFourValue && this.alwaysCompletedSteps.includes(currentKey)) {
                // Always show continue button for these steps
                this.formCompletedValue = true
            } else if (currentKey === "routine_add_products") {
                // v5 lets beginners continue without opening search or adding products.
                this.formCompletedValue = this.onboardingFiveValue || this.productSearchTappedValue || currentStep.dataset.hasRoutineProducts === "true"
            } else {
                this.formCompletedValue = this.stepFormComplete(currentStep)
            }
            if (currentKey === "skin_type_result") this.formCompletedValue = false

            // Show new step with fade in
            this.showCurrentStep()

            // Refresh the routine insights on entry when the user went back and
            // edited their products.
            if (currentKey === "routine_information" && this.routineInsightsStale) {
                this.routineInsightsStale = false
                document.getElementById("onboarding_routine_insights")?.reload()
            }

            // Explicitly call updateContinueButton after setting formCompletedValue
            this.updateContinueButton()
            this.updateSecondaryButton()
        } catch (error) {
            console.error("Error during transition:", error)
        }
    }

    updateFormCompleted() {
        try {
            this.formCompletedValue = true
            this.updateContinueButton()
        } catch (error) {
            console.error("Error updating form completed:", error)
        }
    }

    updateContinueButtonEmphasis(currentStep) {
        if (!this.hasContinueButtonTarget) return

        const deEmphasize = currentStep?.dataset.stepKey === "routine_add_products" &&
            !this.routineHasProductsValue

        if (deEmphasize) {
            this.continueButtonTarget.classList.remove("bg-indigo-900", "text-white")
            this.continueButtonTarget.classList.add("bg-white", "ring-1", "ring-warm-gray-300", "text-warm-gray-800")
        } else {
            this.continueButtonTarget.classList.remove("bg-white", "ring-1", "ring-warm-gray-300", "text-warm-gray-800")
            this.continueButtonTarget.classList.add("bg-indigo-900", "text-white")
        }
    }

    updateContinueButton() {
        try {
            if (!this.hasContinueButtonTarget) return

            const currentStep = this.stepTargets[this.currentValue]
            const currentStepKey = currentStep?.dataset.stepKey

            // An auto-advance step hides the button because picking an option
            // moves the user on by itself. That only holds while the step is
            // unanswered: a step the user has answered before renders with the
            // option already checked, and re-tapping a checked radio fires no
            // change event, so nothing advances and there is nothing to press.
            // Keep the button in that case so the step still has a way out.
            const autoAdvance = currentStep?.dataset.autoAdvance === 'true'
            const alreadyAnswered = autoAdvance && !!currentStep.querySelector(
                'input[type="radio"]:checked, input[type="checkbox"]:checked'
            )

            if (currentStepKey === "register" || (autoAdvance && !alreadyAnswered)) {
                this.continueButtonTarget.classList.add("hidden", "opacity-0")
                this.continueButtonTarget.classList.remove("opacity-100")
                if (this.hasButtonContainerTarget) {
                    this.buttonContainerTarget.classList.add("hidden")
                }
                return
            } else {
                // Ensure button and container are visible if not the register step
                this.continueButtonTarget.classList.remove("hidden", "opacity-0")
                if (this.hasButtonContainerTarget) {
                    this.buttonContainerTarget.classList.remove("hidden")
                }
            }

            // Keep the action visible while making incomplete steps clearly
            // disabled. The next action also guards incomplete question steps,
            // while these attributes provide the right visual and accessible state.
            const nativeReviewDelayActive = this.nativeReviewDelayActive &&
                currentStep?.dataset.nativeReviewPrompt === "true"

            if (this.formCompletedValue && !nativeReviewDelayActive) {
                this.continueButtonTarget.classList.remove("opacity-40", "cursor-not-allowed", "pointer-events-none")
                this.continueButtonTarget.classList.add("opacity-100", "pointer-events-auto")
                this.continueButtonTarget.setAttribute("aria-disabled", "false")
                this.continueButtonTarget.removeAttribute("tabindex")
            } else {
                this.continueButtonTarget.classList.remove("opacity-100", "pointer-events-auto")
                this.continueButtonTarget.classList.add("opacity-40", "cursor-not-allowed", "pointer-events-none")
                this.continueButtonTarget.setAttribute("aria-disabled", "true")
                this.continueButtonTarget.setAttribute("tabindex", "-1")
            }

            // Update button text based on current step
            let buttonText = "Continue"

            if (currentStep.dataset.stepKey === "skin_type_quiz") {
                buttonText = "Find my skin type"
            } else if (currentStep.dataset.stepKey === "skin_profile_generation") {
                // onboarding_5 generates the profile before showing it, so the
                // button names what comes next rather than ending the flow.
                buttonText = this.onboardingFiveValue ? "See my Skin Profile" : "Get started"
            } else if (currentStep.dataset.stepKey === "goal_timeline_preview" && this.onboardingFiveValue) {
                buttonText = "Find what fits my skin"
            } else if (currentStep.dataset.stepKey === "screening_receipt") {
                buttonText = "Add my products"
            } else if (currentStep.dataset.stepKey === "skincare_struggle_affirmation") {
                buttonText = "Let’s find what fits"
            } else if (currentStep.dataset.stepKey === "product_info_demo") {
                buttonText = "Finish profile setup"
            } else if (currentStep.dataset.stepKey === "native_guest_welcome") {
                buttonText = "Get started"
            } else if (currentStep.dataset.stepKey === "quiz") {
                buttonText = "Get Started"
            } else if (currentStep.dataset.stepKey === "skin_goals_affirmation") {
                buttonText = "Let’s do it"
            } else if (currentStep.dataset.stepKey === "routine_projection") {
                buttonText = "Show me my matches"
            } else if (currentStep.dataset.stepKey === "plan_reveal") {
                buttonText = "Continue"
            } else if (currentStep.dataset.stepKey === "profile_value_receipt") {
                buttonText = "Apply it to my routine"
            } else if (currentStep.dataset.stepKey === "routine_step_1_v2") {
                buttonText = "Add my products"
            } else if (currentStep.dataset.stepKey === "routine_add_products") {
                buttonText = this.routineHasProductsValue ? "Done adding products" : "Skip for now"
            } else if (currentStep.dataset.stepKey === "download_skinsort_app") {
                buttonText = "Download the app"
            } else if (currentStep.dataset.stepKey === "preview_1" && currentStep.dataset.freeTrialCopyEnabled === "true") {
                // Platforms that still render the previews retain the
                // existing free-trial framing.
                buttonText = "Continue for free"
            } else if (!this.onboardingFourValue && (currentStep.dataset.stepKey === "skin_type_result" || currentStep.dataset.stepKey === "skin_type_known")) {
                buttonText = this.concernDrilldownButtonText() || buttonText
            }

            this.continueButtonTarget.textContent = buttonText

            // Leaving the product step without adding anything is an exit, not
            // the goal, so it should not be the most prominent control on the
            // screen while the routine is still empty.
            this.updateContinueButtonEmphasis(currentStep)

            // Check if this is the last step
            const isLastStep = this.currentValue === this.stepTargets.length - 1

            // Special handling for download_skinsort_app step
            if (currentStepKey === "download_skinsort_app") {
                const isAndroid = /android/i.test(navigator.userAgent)
                if (isAndroid) {
                    this.continueButtonTarget.href = "https://play.google.com/store/apps/details?id=com.skinsort&utm_source=skinsort.com&utm_medium=web&utm_campaign=OnboardingStep"
                } else {
                    this.continueButtonTarget.href = "https://apps.apple.com/app/apple-store/id6478040418?pt=126899204&ct=OnboardingStep&mt=8"
                }
                this.continueButtonTarget.removeAttribute("data-action")
                this.continueButtonTarget.setAttribute("data-is-last-step", "false")
                this.continueButtonTarget.setAttribute("target", "_blank")
            } else if (currentStepKey === "last_step") {
                if (isLastStep) {
                    this.continueButtonTarget.href = "/premium?placement=onboarding_end&source=onboarding_end"
                    this.continueButtonTarget.removeAttribute("data-action")
                    this.continueButtonTarget.setAttribute("data-is-last-step", "true")
                } else {
                    this.continueButtonTarget.href = "#"
                    this.continueButtonTarget.setAttribute("data-action", "click->onboarding#next")
                    this.continueButtonTarget.setAttribute("data-is-last-step", "false")
                }
            } else if (isLastStep) {
                // Handle actual last step
                this.continueButtonTarget.href = this.onboardingExitHref()
                this.continueButtonTarget.removeAttribute("data-action")
                this.continueButtonTarget.setAttribute("data-is-last-step", "true")
            } else {
                this.continueButtonTarget.href = "#"
                this.continueButtonTarget.setAttribute("data-action", "click->onboarding#next")
                this.continueButtonTarget.setAttribute("data-is-last-step", "false")
            }
        } catch (error) {
            console.error("Error updating continue button:", error)
        }
    }

    updateSecondaryButton() {
        try {
            if (!this.hasSecondaryButtonTarget) return

            // Show/hide the secondary button based on the current step
            const currentKey = this.stepTargets[this.currentValue].dataset.stepKey
            this.signInLinkTarget.classList.add("hidden")

            if (currentKey === "native_guest_welcome" && this.showAuthButtonValue) {
                this.secondaryButtonTarget.classList.add("hidden")
                this.signInLinkTarget.classList.remove("hidden")
            } else if (currentKey === "skin_type_quiz") {
                // onboarding_4 asks the same question outright on
                // skin_type_known_fork, so it needs no hidden shortcut here.
                this.showSecondaryAction("I know my skin type", "click->onboarding#skipToKnownSkinType")
            } else if (currentKey === "routine_step_1_v2") {
                this.showSecondaryAction("I don't have any yet", "click->onboarding#skipToNoRoutine")
            } else if (currentKey === "download_skinsort_app") {
                this.showSecondaryAction("Continue on web", "click->onboarding#next")
            } else {
                this.secondaryButtonTarget.classList.add("hidden")
            }
        } catch (error) {
            console.error("Error updating secondary button:", error)
        }
    }

    showSecondaryAction(text, action) {
        this.secondaryButtonTarget.classList.remove("hidden")
        this.secondaryButtonTarget.textContent = text
        this.secondaryButtonTarget.href = "#"
        this.secondaryButtonTarget.dataset.action = action
        this.secondaryButtonTarget.removeAttribute("target")
        this.secondaryButtonTarget.removeAttribute("rel")
    }

    skipToKnownSkinType(event) {
        if (event) event.preventDefault()

        try {
            this.usedKnownSkinTypeValue = true
            this.totalValue = this.relevantStepTargets().length

            // Find the index of the skin_type_known step
            const knownSkinTypeIndex = this.stepTargets.findIndex(step =>
                step.dataset.stepKey === "skin_type_known"
            )

            if (knownSkinTypeIndex !== -1) {
                this.currentValue = knownSkinTypeIndex
                this.transition()
            }
        } catch (error) {
            console.error("Error skipping to known skin type:", error)
        }
    }

    navigateFromNoRoutineAffirmation(event) {
        if (event) event.preventDefault()

        try {
            if (this.onboardingFourValue) {
                this.advanceToStep("skin_profile_generation")
                return
            }

            // No-routine users skip the audit screens but still get the
            // projection (its starter variant pitches the routine we'd build
            // for them) before the paywall. routine_projection only renders in
            // the onboarding_3b enabled variant, so control and premium users
            // fall through to last_step / preview_1 as before.
            const targetIndex = this.stepTargets.findIndex(step =>
                step.dataset.stepKey === "routine_projection" ||
                step.dataset.stepKey === "last_step" ||
                step.dataset.stepKey === "preview_1"
            )

            if (targetIndex !== -1) {
                this.currentValue = targetIndex
                this.transition()
            } else {
                const exitUrl = this.onboardingExitHref()
                if (window.Turbo?.visit) {
                    window.Turbo.visit(exitUrl)
                } else {
                    window.location.href = exitUrl
                }
            }
        } catch (error) {
            console.error("Error navigating from no-routine affirmation:", error)
        }
    }

    skipToNoRoutine(event) {
        if (event) event.preventDefault()

        try {
            // Reached from the routine introduction, which onboarding_5 does
            // not render: there, a user with no products simply continues off
            // the product screen into the starter projection.
            // Find the index of the no-routine affirmation step
            const noRoutineIndex = this.stepTargets.findIndex(step =>
                step.dataset.stepKey === "no_routine_affirmation"
            )

            if (noRoutineIndex !== -1) {
                this.currentValue = noRoutineIndex
                this.transition()
            }
        } catch (error) {
            console.error("Error skipping to no routine:", error)
        }
    }

    navigateToSkinTypeQuiz(event) {
        if (event) event.preventDefault()

        try {
            this.usedKnownSkinTypeValue = false
            this.totalValue = this.relevantStepTargets().length

            const skinTypeQuizIndex = this.stepTargets.findIndex(step =>
                step.dataset.stepKey === this.firstQuizStepKey()
            )

            if (skinTypeQuizIndex !== -1) {
                if (this.stepFormComplete(this.stepTargets[skinTypeQuizIndex])) this.submitForm(this.stepTargets[skinTypeQuizIndex].querySelector("form"))
                this.currentValue = skinTypeQuizIndex
                this.transition()
            }
        } catch (error) {
            console.error("Error navigating to skin type quiz:", error)
        }
    }

    // onboarding_4 asks "Do you know your skin type?" outright instead of
    // hiding the shortcut behind a secondary button on the quiz intro.
    // "I know it" goes to the skin type picker, anything else runs the quiz.
    navigateFromSkinTypeKnownFork(event) {
        if (event) event.preventDefault()

        try {
            const answer = document.querySelector('input[name="onboarding_step[data][skin_type_known_fork]"]:checked')?.value
            const knowsSkinType = answer === "knows_skin_type"

            this.usedKnownSkinTypeValue = knowsSkinType
            this.totalValue = this.relevantStepTargets().length
            if (!knowsSkinType) {
                const first = this.stepTargets.find(step => step.dataset.stepKey === this.firstQuizStepKey())
                if (this.stepFormComplete(first)) this.submitForm(first.querySelector("form"))
            }
            this.advanceToStep(knowsSkinType ? "skin_type_known" : this.firstQuizStepKey())
        } catch (error) {
            console.error("Error navigating from the skin type fork:", error)
        }
    }

    navigateToConcernDrilldown(event) {
        if (event) event.preventDefault()

        try {
            const concernDrilldownIndex = this.stepTargets.findIndex(step =>
                step.dataset.stepKey === "post_skin_goal_follow_up_1"
            )

            if (concernDrilldownIndex !== -1 && this.isRelevantStep(this.stepTargets[concernDrilldownIndex])) {
                this.currentValue = concernDrilldownIndex
                this.transition()
            } else {
                const skinTypeResultIndex = this.stepTargets.findIndex(step =>
                    step.dataset.stepKey === "skin_type_result"
                )
                const nextStepIndex = this.nextRelevantStepIndex(skinTypeResultIndex, 1)

                if (nextStepIndex !== -1) {
                    this.currentValue = nextStepIndex
                    this.transition()
                } else {
                    this.trackPaywallPresented("onboarding_end")
                    window.location.href = "/premium?placement=onboarding_end&source=onboarding_short"
                }
            }
        } catch (error) {
            console.error("Error navigating to concern drilldown:", error)
        }
    }

    // "Next: clearing breakouts"-style continue text announcing the pivot from
    // the skin type section into the concern drilldown. Returns null when the
    // drilldown doesn't apply (control variant, or no panel for the goal), so
    // the button keeps its default text.
    concernDrilldownButtonText() {
        const followUpStep = this.stepTargets.find(step =>
            step.dataset.stepKey === "post_skin_goal_follow_up_1"
        )
        const label = followUpStep
            ?.querySelector(`[data-skin-goal-follow-up-panel="${this.skinGoalValue}"]`)
            ?.dataset.skinGoalFollowUpLabel

        return label ? `Next: ${label}` : null
    }

    stepFormComplete(step) {
        if (!step) return false
        const form = step.querySelector("form")
        if (!form) return false
        const key = step.dataset.stepKey
        const radios = [...form.querySelectorAll('input[type="radio"]:not(:disabled):checked')]
        const boxes = [...form.querySelectorAll('input[type="checkbox"]:not(:disabled):checked')]
        if (key === "skin_type_quiz_v2_shine") return new Set(radios.map(input => input.name)).size === 3
        if (key === "skin_type_quiz_v2_barrier" || key === "lifestyle_preferences" ||
            form.dataset.requiredMultiSelect === "true") return boxes.length > 0
        return radios.length > 0 || boxes.length > 0 || [...form.querySelectorAll("select:not(:disabled)")].some(select => select.value)
    }

    async loadSkinTypeResultCard(retry = false) {
        if (!this.hasSkinTypeResultFrameTarget) return
        const attempt = {}
        this.resultLoadAttempt = attempt
        this.formCompletedValue = false
        this.updateContinueButton()
        this.skinTypeResultFrameTarget.classList.add("hidden")
        this.resultSaveStatusTarget.classList.remove("hidden")
        this.resultSaveMessageTarget.textContent = "Saving your answers..."
        this.resultSaveRetryTarget.classList.add("hidden")
        if (retry) {
            this.answerSaveFailed = false
            for (const [stepKey, formData] of [...this.pendingAnswers]) this.queueAnswer(stepKey, formData)
        }
        await this.submissionQueue
        if (this.resultLoadAttempt !== attempt || this.disconnected) return
        this.resultSaveRetryTarget.dispatchEvent(new CustomEvent("loading-button:stop"))
        if (this.answerSaveFailed || this.pendingAnswers.size > 0) {
            this.showResultSaveError()
            return
        }
        this.resultSaveMessageTarget.textContent = "Reading your skin profile..."
        const frame = this.skinTypeResultFrameTarget
        const url = new URL(frame.dataset.src, window.location.origin)
        url.searchParams.set("t", Date.now())
        frame.src = url.toString()
    }

    retrySkinTypeResult() {
        return this.loadSkinTypeResultCard(true)
    }

    showResultSaveError(event) {
        event?.preventDefault()
        if (this.stepTargets[this.currentValue]?.dataset.stepKey !== "skin_type_result") return
        this.formCompletedValue = false
        this.skinTypeResultFrameTarget.classList.add("hidden")
        this.resultSaveStatusTarget.classList.remove("hidden")
        this.resultSaveMessageTarget.textContent = "We couldn't save or read all your answers. Check your connection and try again."
        this.resultSaveRetryTarget.classList.remove("hidden")
        this.resultSaveRetryTarget.dispatchEvent(new CustomEvent("loading-button:stop"))
        this.updateContinueButton()
    }

    skinTypeResultLoaded(event) {
        if (event.target !== this.skinTypeResultFrameTarget || !this.resultLoadAttempt ||
            this.stepTargets[this.currentValue]?.dataset.stepKey !== "skin_type_result") return
        const label = event.target.querySelector("[data-skin-type-result-label]")?.dataset.skinTypeResultLabel
        if (label) this.skinTypeValue = label.toLowerCase()
        this.resultSaveStatusTarget.classList.add("hidden")
        this.skinTypeResultFrameTarget.classList.remove("hidden")
        this.formCompletedValue = !!event.target.querySelector('[data-skin-type-result-complete="true"]')
        this.updateContinueButton()
    }

    // Handle form updates
    handleFormChange(event) {
        try {
            const currentStep = this.stepTargets[this.currentValue];
            const form = event.target.closest('form');

            if (!form) return;

            const stepKey = currentStep.dataset.stepKey;
            this.enforceExclusiveMultiSelect(form, event.target)
            if (stepKey === 'skin_type_known') {
                this.skinTypeValue = event.target.value
            } else if (stepKey === 'where_did_you_hear_about_us') {
                const value = form.querySelector('input[name="onboarding_step[data][referral_source]"]:checked')?.value;
                if (value) this.referralSourceValue = value;
            } else if (stepKey === 'referral_source_detail') {
                const value = form.querySelector('input[name="onboarding_step[data][referral_source_detail]"]:checked')?.value;
                if (value) this.referralSourceDetailValue = value;
            } else if (stepKey === 'skin_goals') {
                // Changing the primary goal invalidates any follow-up answers
                // captured for a different goal's questions.
                this.followUpAnswers = {};
                this.skinGoalValue = event.target.value;
                this.syncSkinGoalAffirmation(event.target);
                this.syncSkinGoalFollowUps();
                this.syncSecondaryConcernConfirm();
            } else if (stepKey === 'user_jobs_to_be_done_v2') {
                // Kept current the same way the goal is: the timeline preview
                // reads it two screens later, before any reload could reseed it.
                this.obstacleValue = event.target.value;
                this.syncGoalTimelinePreview();
            } else if (stepKey === 'sun_exposure') {
                this.syncSunExposureAffirmation(event.target);
            } else if (stepKey === 'region_preference') {
                this.enforceRegionSelection(currentStep, event.target);
            } else if (stepKey === 'lifestyle_preferences') {
                this.enforceLifestylePreferenceSelection(currentStep, event.target);
            } else if (stepKey === 'diagnosed_conditions') {
                this.enforceDiagnosedConditionsSelection(currentStep, event.target);
                this.syncDiagnosedConditionsAffirmation();
                this.syncConditionScreenResult();
                // Selecting or clearing conditions changes whether the
                // affirmation step is relevant, so the step total moves too.
                this.totalValue = this.relevantStepTargets().length;
            } else if (['skin_type_quiz_v2_reactivity', 'skin_type_known_reactivity'].includes(stepKey)) {
                // "Never" skips the triggers screen, so the step total moves.
                this.totalValue = this.relevantStepTargets().length;
            } else if (this.isSkinGoalFollowUp(currentStep)) {
                this.recordFollowUpAnswer(currentStep, event.target);
                this.syncSecondaryConcernConfirm();
            }

            const wasComplete = this.formCompletedValue
            this.formCompletedValue = this.stepFormComplete(currentStep)
            this.updateContinueButton();

            // Submit form asynchronously
            const submission = this.submitForm(form);

            if (this.formCompletedValue && currentStep?.dataset.autoAdvance === 'true' &&
                (stepKey !== 'skin_type_quiz_v2_shine' || !wasComplete)) {
                this.scheduleAutoAdvance(currentStep, submission);
            }
        } catch (error) {
            console.error("Error handling form change:", error);
        }
    }

    syncSkinGoalAffirmation(input = null) {
        const panels = this.element.querySelectorAll('[data-skin-goal-affirmation-panel]')
        if (panels.length === 0) return

        const selectedInput = input || this.element.querySelector(
            'input[name="onboarding_step[data][skin_goal]"]:checked'
        )
        const goal = selectedInput?.value || this.skinGoalValue
        const hasMatch = Array.from(panels).some(panel => panel.dataset.skinGoalAffirmationPanel === goal)

        panels.forEach(panel => {
            const key = panel.dataset.skinGoalAffirmationPanel
            const active = hasMatch ? key === goal : key === 'default'

            panel.classList.toggle('hidden', !active)
        })
    }

    syncSunExposureAffirmation(input = null) {
        const panels = this.element.querySelectorAll('[data-sun-exposure-affirmation-panel]')
        if (panels.length === 0) return

        const selectedInput = input || this.element.querySelector(
            'input[name="onboarding_step[data][sun_exposure]"]:checked'
        )
        const value = selectedInput?.value
        const hasMatch = Array.from(panels).some(panel => panel.dataset.sunExposureAffirmationPanel === value)

        panels.forEach(panel => {
            const key = panel.dataset.sunExposureAffirmationPanel
            const active = hasMatch ? key === value : key === 'default'

            panel.classList.toggle('hidden', !active)
        })
    }

    // The diagnosed-condition keys currently selected, read from the checkbox
    // form (rendered with the user's saved selections), falling back to the
    // server-seeded value only when the form isn't rendered. "none" never
    // counts as a condition.
    selectedDiagnosedConditions() {
        const boxes = Array.from(this.element.querySelectorAll(
            'input[type="checkbox"][name^="onboarding_step[data][diagnosed_conditions]"]'
        ))

        // When the checkbox form isn't in the DOM, use the server-seeded value.
        // When it is present, the live checkboxes are the source of truth even
        // if the user has unchecked everything (so clearing them sticks).
        if (boxes.length === 0) {
            return this.diagnosedConditionsValue.filter(key => key !== 'none')
        }

        return boxes
            .filter(cb => cb.checked)
            .map(cb => cb.name.match(/\[diagnosed_conditions\]\[(\w+)\]$/)?.[1])
            .filter(key => key && key !== 'none')
    }

    syncDiagnosedConditionsAffirmation() {
        const panels = this.element.querySelectorAll('[data-diagnosed-conditions-affirmation-panel]')
        if (panels.length === 0) return

        const selected = this.selectedDiagnosedConditions()
        const primary = this.DIAGNOSED_CONDITION_PRIORITY.find(key => selected.includes(key))
        const hasMatch = primary && Array.from(panels).some(
            panel => panel.dataset.diagnosedConditionsAffirmationPanel === primary
        )

        panels.forEach(panel => {
            const key = panel.dataset.diagnosedConditionsAffirmationPanel
            const active = hasMatch ? key === primary : key === 'default'

            panel.classList.toggle('hidden', !active)
        })

        const chipsContainer = this.element.querySelector('[data-diagnosed-conditions-affirmation-chips]')
        if (!chipsContainer) return

        const others = selected.filter(key => key !== primary)
        chipsContainer.classList.toggle('hidden', others.length === 0)
        chipsContainer.querySelectorAll('[data-diagnosed-conditions-affirmation-chip]').forEach(chip => {
            chip.classList.toggle('hidden', !others.includes(chip.dataset.diagnosedConditionsAffirmationChip))
        })
    }

    syncConditionScreenResult() {
        const panels = this.element.querySelectorAll('[data-condition-screen-result-panel]')
        if (panels.length === 0) return

        const selected = this.selectedDiagnosedConditions()
        const primary = this.DIAGNOSED_CONDITION_PRIORITY.find(key => selected.includes(key))
        const hasMatch = primary && Array.from(panels).some(
            panel => panel.dataset.conditionScreenResultPanel === primary
        )

        panels.forEach(panel => {
            const key = panel.dataset.conditionScreenResultPanel
            const active = hasMatch ? key === primary : key === 'default'

            panel.classList.toggle('hidden', !active)
        })

        this.element.querySelectorAll('[data-condition-screen-result-header]').forEach(header => {
            const key = header.dataset.conditionScreenResultHeader
            const active = selected.length > 0 ? key === 'diagnosed' : key === 'default'

            header.classList.toggle('hidden', !active)
        })

        const names = this.element.querySelector('[data-condition-screen-result-condition-names]')
        if (names && selected.length > 0) {
            names.textContent = this.conditionListSentence(selected)
        }
    }

    // Mirrors the server's humanize + to_sentence for condition keys, e.g.
    // ["fungal_acne", "eczema"] -> "fungal acne and eczema".
    conditionListSentence(keys) {
        const words = keys.map(key => key.replace(/_/g, ' '))
        if (words.length <= 1) return words.join('')
        if (words.length === 2) return words.join(' and ')

        return `${words.slice(0, -1).join(', ')}, and ${words[words.length - 1]}`
    }

    // Shared panel swap for the answer-echo screens: shows the panel matching
    // the source question's checked radio (falling back to the server-seeded
    // answer, then to the "default" panel). Multiple elements may share a
    // panel key, e.g. a headline and a copy block that swap together.
    applyAnswerPanels(attributeName, inputName, fallback) {
        const panels = this.element.querySelectorAll(`[${attributeName}]`)
        if (panels.length === 0) return

        const value = this.element.querySelector(
            `input[name="onboarding_step[data][${inputName}]"]:checked`
        )?.value || fallback
        const hasMatch = Array.from(panels).some(panel => panel.getAttribute(attributeName) === value)

        panels.forEach(panel => {
            const key = panel.getAttribute(attributeName)
            const active = hasMatch ? key === value : key === 'default'

            panel.classList.toggle('hidden', !active)
        })
    }

    syncWastedSpendReality() {
        this.applyAnswerPanels('data-wasted-spend-reality-panel', 'wasted_spend', this.wastedSpendValue)
    }

    // The timeline screen carries two independent panel sets: the per-goal
    // copy control and onboarding_4 use, and the onboarding_5 headline keyed
    // off the obstacle the user just named. Both are applied every time, so
    // the goal-keyed week captions stay right in onboarding_5 too.
    syncGoalTimelinePreview() {
        this.applyAnswerPanels('data-goal-timeline-panel', 'skin_goal', this.skinGoalValue)
        this.applyAnswerPanels('data-goal-timeline-obstacle-panel', 'user_jobs_to_be_done', this.obstacleValue)
    }

    // onboarding_5 asks the struggle question after the goal, so its title
    // names the goal. No panels exist in the other variants, which leaves this
    // a no-op there.
    syncSkincareStruggle() {
        this.applyAnswerPanels('data-skincare-struggle-panel', 'skin_goal', this.skinGoalValue)
    }

    // onboarding_5 asks for reminders after the takeaway; its copy
    // names the outcome the reminders are keeping on track.
    syncNotificationPermission() {
        this.applyAnswerPanels('data-notification-permission-panel', 'skin_goal', this.skinGoalValue)
    }

    syncHeldBackAffirmation() {
        this.applyAnswerPanels('data-held-back-affirmation-panel', 'held_back', this.heldBackValue)
    }

    syncSkinProfileGeneration() {
        this.applyAnswerPanels('data-skin-profile-generation-goal-panel', 'skin_goal', this.skinGoalValue)
    }

    // The receipt's copy is written by syncOnboardingProfileValueReceipt,
    // defined in onboarding_steps/show so it always ships with the current
    // HTML: native WebViews can retain an older compiled controller, and a
    // receipt personalized only from here silently stays on its generic copy
    // there. Calling it on every step view keeps back-nav changes reflected.
    syncProfileValueReceipt() {
        window.syncOnboardingProfileValueReceipt?.()
    }

    syncCommitment() {
        const panels = this.element.querySelectorAll('[data-commitment-panel]')
        if (panels.length === 0) return

        const goal = this.skinGoalValue
        const hasMatch = Array.from(panels).some(panel => panel.dataset.commitmentPanel === goal)

        panels.forEach(panel => {
            const key = panel.dataset.commitmentPanel
            const active = hasMatch ? key === goal : key === 'default'

            panel.classList.toggle('hidden', !active)
        })

        if (!this.onboardingFourValue) return

        const meaning = this.element.querySelector(
            'input[name="onboarding_step[data][what_it_means]"]:checked'
        )?.value
        const reasons = {
            not_thinking_about_skin: "For the next 30 days, I’ll follow my plan so skincare takes up less mental space.",
            confident_without_makeup: "For the next 30 days, I’ll follow my plan and build consistency I can feel good about.",
            stop_wasting_money: "For the next 30 days, I’ll follow my plan and learn which products deserve a place in my routine.",
            know_routine_is_right: "For the next 30 days, I’ll follow my plan and learn what works for my skin."
        }
        const reason = reasons[meaning] || "For the next 30 days, I’ll follow my plan and learn what works for my skin."
        this.element.querySelectorAll('[data-commitment-reason]').forEach(target => {
            target.textContent = reason
        })
    }

    syncSkinGoalFollowUps() {
        this.stepTargets.filter(step => this.isSkinGoalFollowUp(step)).forEach(step => {
            let activePanel = null

            step.querySelectorAll('[data-skin-goal-follow-up-panel]').forEach(panel => {
                const active = panel.dataset.skinGoalFollowUpPanel === this.skinGoalValue

                panel.classList.toggle('hidden', !active)
                panel.querySelectorAll('input, select, textarea, button').forEach(input => {
                    input.disabled = !active
                })

                if (active) activePanel = panel
            })

            step.dataset.autoAdvance = activePanel?.dataset.autoAdvance || 'false'
        })

        this.totalValue = this.relevantStepTargets().length
    }

    isSkinGoalFollowUp(step) {
        return Boolean(step?.querySelector('[data-skin-goal-follow-up-position]'))
    }

    isSecondaryConcernConfirm(step) {
        return Boolean(step?.querySelector('[data-secondary-concern-confirm]'))
    }

    // Serialized rules for the secondary concern confirm step, shaped as
    // { goal: [{ question, answers, secondary, panelKey }] }. Parsed once.
    secondaryConcernRules() {
        if (this._secondaryConcernRules) return this._secondaryConcernRules

        const step = this.stepTargets.find(target => this.isSecondaryConcernConfirm(target))
        const element = step?.querySelector('[data-secondary-concern-confirm]')

        try {
            this._secondaryConcernRules = element ? JSON.parse(element.dataset.secondaryConcernConfirm) : {}
        } catch (error) {
            console.error("Error parsing secondary concern rules:", error)
            this._secondaryConcernRules = {}
        }

        return this._secondaryConcernRules
    }

    // Seed the client answer map from any server pre-checked follow-up inputs so
    // a mid-flow page reload can still derive the secondary concern step. Hidden
    // panels are disabled by syncSkinGoalFollowUps, so only the active goal's
    // answers are read.
    seedFollowUpAnswers() {
        this.followUpAnswers = {}

        this.stepTargets.filter(step => this.isSkinGoalFollowUp(step)).forEach(step => {
            step.querySelectorAll('input:checked:not(:disabled)').forEach(input => {
                this.recordFollowUpAnswer(step, input)
            })
        })
    }

    recordFollowUpAnswer(step, target) {
        if (!target?.name) return

        const match = target.name.match(/onboarding_step\[data\]\[([^\]]+)\]/)
        if (!match) return

        const questionKey = match[1]

        if (target.name.endsWith('[]')) {
            this.followUpAnswers[questionKey] = Array.from(
                step.querySelectorAll(`input[name="${target.name}"]:checked`)
            ).map(input => input.value)
        } else {
            this.followUpAnswers[questionKey] = [target.value]
        }
    }

    // First matching rule (file order = priority) for the current goal wins,
    // mirroring the server's OnboardingStep::SecondaryConcernRules#match.
    evaluateSecondaryConcern() {
        this.activeSecondaryPanelKey = null

        if (!this.skinGoalValue) return

        const rules = this.secondaryConcernRules()[this.skinGoalValue] || []
        const match = rules.find(rule => {
            const answers = this.followUpAnswers[rule.question] || []
            return rule.answers.some(answer => answers.includes(answer))
        })

        if (match) this.activeSecondaryPanelKey = match.panelKey
    }

    syncSecondaryConcernConfirm() {
        this.evaluateSecondaryConcern()

        const step = this.stepTargets.find(target => this.isSecondaryConcernConfirm(target))
        if (step) {
            step.querySelectorAll('[data-secondary-concern-panel]').forEach(panel => {
                const active = panel.dataset.secondaryConcernPanel === this.activeSecondaryPanelKey

                panel.classList.toggle('hidden', !active)
                panel.querySelectorAll('input, select, textarea, button').forEach(input => {
                    input.disabled = !active
                })
            })
        }

        this.totalValue = this.relevantStepTargets().length
    }

    // Region multi-select: "anywhere" is mutually exclusive with the specific
    // regions, and at most MAX_REGION_SELECTIONS regions may be picked. Runs
    // before the form submits, so the corrected checkbox state is what gets
    // persisted. The server (StoreOnboardingDataOnUser) re-enforces the same
    // rules for hand-crafted requests.
    enforceRegionSelection(step, toggled) {
        const checkboxes = Array.from(
            step.querySelectorAll('input[type="checkbox"][name="onboarding_step[data][region_preference][]"]')
        )
        const anywhere = checkboxes.find(cb => cb.value === 'anywhere')
        const regions = checkboxes.filter(cb => cb.value !== 'anywhere')

        if (toggled.value === 'anywhere') {
            // Selecting "anywhere" clears every specific region.
            if (toggled.checked) regions.forEach(cb => { cb.checked = false })
            return
        }

        // Selecting a specific region clears "anywhere".
        if (toggled.checked && anywhere) anywhere.checked = false

        // Cap the specific-region picks: revert the toggle that exceeds the max.
        const selectedRegions = regions.filter(cb => cb.checked)
        if (selectedRegions.length > this.MAX_REGION_SELECTIONS) {
            toggled.checked = false
        }
    }

    // Lifestyle preferences multi-select: "No must-haves for me" is mutually
    // exclusive with the specific standards. Runs before the form submits, so
    // the corrected checkbox state is what gets persisted. The server
    // (StoreOnboardingDataOnUser) re-enforces the same rule for hand-crafted
    // requests.
    enforceLifestylePreferenceSelection(step, toggled) {
        const checkboxes = Array.from(
            step.querySelectorAll('input[type="checkbox"][name^="onboarding_step[data][product_preferences]"]')
        )
        const none = checkboxes.find(cb => cb.name === 'onboarding_step[data][product_preferences][no_preferences]')
        const standards = checkboxes.filter(cb => cb !== none)

        if (toggled === none) {
            // Selecting "No must-haves" clears every specific standard.
            if (toggled.checked) standards.forEach(cb => { cb.checked = false })
            return
        }

        // Selecting any specific standard clears "No must-haves".
        if (toggled.checked && none) none.checked = false
    }

    // Diagnosed conditions multi-select: "None of these" is mutually exclusive
    // with the specific conditions. Runs before the form submits, so the
    // corrected checkbox state is what gets persisted. The server
    // (StoreOnboardingDataOnUser) re-enforces the same rule for hand-crafted
    // requests.
    enforceDiagnosedConditionsSelection(step, toggled) {
        const checkboxes = Array.from(
            step.querySelectorAll('input[type="checkbox"][name^="onboarding_step[data][diagnosed_conditions]"]')
        )
        const none = checkboxes.find(cb => cb.name === 'onboarding_step[data][diagnosed_conditions][none]')
        const conditions = checkboxes.filter(cb => cb !== none)

        if (toggled === none) {
            // Selecting "None of these" clears every specific condition.
            if (toggled.checked) conditions.forEach(cb => { cb.checked = false })
            return
        }

        // Selecting any specific condition clears "None of these".
        if (toggled.checked && none) none.checked = false
    }

    enforceExclusiveMultiSelect(form, toggled) {
        const exclusiveValue = form.dataset.exclusiveOption
        if (!exclusiveValue || toggled.type !== "checkbox") return

        const checkboxes = Array.from(form.querySelectorAll('input[type="checkbox"]'))
        const exclusive = checkboxes.find(checkbox => checkbox.name.endsWith(`[${exclusiveValue}]`))
        if (!exclusive) return

        if (toggled === exclusive && toggled.checked) {
            checkboxes.filter(checkbox => checkbox !== exclusive).forEach(checkbox => { checkbox.checked = false })
        } else if (toggled.checked) {
            exclusive.checked = false
        }
    }

    isRelevantStep(step) {
        if (step.dataset.stepKey === "skin_type_known_triggers") {
            return this.usedKnownSkinTypeValue && this.reactivityAnswer("skin_type_known_reactivity") !== "never"
        }
        if (step.dataset.stepKey === "skin_type_quiz_v2_triggers") {
            return !this.usedKnownSkinTypeValue && this.reactivityAnswer("skin_type_quiz_v2_reactivity") !== "never"
        }
        if (step.dataset.stepKey === "skin_type_known_reactivity") return this.usedKnownSkinTypeValue
        if (step.dataset.stepKey.startsWith("skin_type_quiz_v2_") || V1_QUIZ_STEP_KEYS.includes(step.dataset.stepKey)) return !this.usedKnownSkinTypeValue

        if (this.isSecondaryConcernConfirm(step)) {
            return this.activeSecondaryPanelKey !== null
        }

        if (this.onboardingFourValue && this.usedKnownSkinTypeValue && step.dataset.stepKey === "skin_type_result") {
            return false
        }

        // skin_type_result explains a quiz-derived answer. It is irrelevant
        // when the user supplied their skin type directly, except in
        // onboarding_5, where the picker leads into the same card and it
        // explains what the picked type will be used for instead.
        if (step.dataset.stepKey === 'skin_type_result') {
            return this.onboardingFiveValue || !this.usedKnownSkinTypeValue
        }

        // Condition affirmation screens only apply when the user selected at
        // least one diagnosed condition ("None of these" users skip them).
        if (['diagnosed_conditions_affirmation', 'condition_screen_result'].includes(step.dataset.stepKey)) {
            return this.selectedDiagnosedConditions().length > 0
        }

        if (!this.isSkinGoalFollowUp(step)) return true
        if (!this.skinGoalValue) return false

        return step.querySelector(`[data-skin-goal-follow-up-panel="${this.skinGoalValue}"]`) !== null
    }

    // Outside onboarding_5 a user-selected skin type skips the quiz result
    // screen, which only explains quiz answers. onboarding 4 has already
    // completed its goal drilldown, so it continues forward into the remaining
    // profile questions. The legacy flow still branches into the concern
    // drilldown here.
    navigateFromKnownSkinTypeBranch(event) {
        // onboarding_5 shows the result card to everyone. The picked type is
        // what it reads out, in place of the quiz answers.
        if (this.onboardingFiveValue) {
            this.advanceToStep("skin_type_result")
            return
        }

        if (this.onboardingFourValue) {
            this.advanceToStep("diagnosed_conditions")
            return
        }

        const skinGoalsIndex = this.stepTargets.findIndex(step =>
            step.dataset.stepKey === "skin_goals"
        )

        if (skinGoalsIndex > this.currentValue) {
            this.advanceToStep("skin_goals")
        } else {
            this.navigateToConcernDrilldown(event)
        }
    }

    reactivityAnswer(stepKey) {
        const step = this.stepTargets.find(target => target.dataset.stepKey === stepKey)
        return step?.querySelector('input[name$="[reactivity]"]:checked')?.value
    }

    firstQuizStepKey() {
        return this.skinTypeQuizV2Value ? "skin_type_quiz_v2_shine" : V1_QUIZ_STEP_KEYS[0]
    }

    // The known-skin-type branch ends on triggers unless reactivity was "Never".
    // The quiz test's control arm ends on the picker itself.
    lastKnownSkinTypeStepKey() {
        if (!this.skinTypeQuizV2Value) return "skin_type_known"
        return this.reactivityAnswer("skin_type_known_reactivity") === "never" ? "skin_type_known_reactivity" : "skin_type_known_triggers"
    }

    relevantStepTargets() {
        return this.stepTargets.filter(step => this.isRelevantStep(step))
    }

    // Keep the rendered questions for a stable progress bar, but never reopen
    // them after generation: the server would reject their replacement writes.
    get profileNavigationFloor() {
        if (!this.onboardingFiveValue || !this.profileCompletedValue) return -1

        return this.stepTargets.findIndex(step => step.dataset.stepKey === "skin_profile_generation")
    }

    nextRelevantStepIndex(currentIndex, direction) {
        let index = currentIndex + direction

        while (index >= 0 && index < this.stepTargets.length) {
            if (this.isRelevantStep(this.stepTargets[index])) return index
            index += direction
        }

        return -1
    }

    cancelAutoAdvance() {
        if (this.autoAdvanceTimer) clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceAttempt = null
    }

    scheduleAutoAdvance(step, submission) {
        this.cancelAutoAdvance()
        const attempt = {}
        this.autoAdvanceAttempt = attempt
        const advance = () => {
            if (this.autoAdvanceAttempt !== attempt || this.disconnected ||
                this.stepTargets[this.currentValue] !== step) return

            this.cancelAutoAdvance()
            this.next()
        }

        this.autoAdvanceTimer = setTimeout(advance, ADVANCE_WAIT_MS + 150)
        submission.then(() => {
            if (this.autoAdvanceAttempt !== attempt) return

            clearTimeout(this.autoAdvanceTimer)
            this.autoAdvanceTimer = setTimeout(advance, 150)
        })
    }

    submitForm(form) {
        if (!form) return Promise.resolve()

        const stepKey = form.closest('[data-onboarding-target="step"]')?.dataset.stepKey
        if (!stepKey) return Promise.resolve()

        return this.queueAnswer(stepKey, new FormData(form))
    }

    queueAnswer(stepKey, formData) {
        // Retain the latest snapshot until it succeeds, including on timeout
        // or an HTTP error. An older save must not clear a newer pending answer.
        // Keep the latest snapshots in answer order, including branch changes.
        this.pendingAnswers.delete(stepKey)
        this.pendingAnswers.set(stepKey, formData)
        this.submissionQueue = this.submissionQueue.then(async () => {
            // Later answers can derive profile data from earlier ones (for
            // example, the final skin-type quiz answer). Keep them pending
            // after a failure so retry preserves that dependency order.
            if (this.answerSaveFailed) return

            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
            if (!csrfToken) throw new Error("CSRF token not found")

            const abort = new AbortController()
            const timeout = setTimeout(() => abort.abort(), SUBMIT_TIMEOUT_MS)
            try {
                const response = await fetch(`/onboarding/${stepKey}`, {
                    method: "PUT",
                    body: formData,
                    headers: {
                        "X-CSRF-Token": csrfToken,
                        "Accept": "application/json"
                    },
                    credentials: 'same-origin',
                    signal: abort.signal
                })
                if (!response.ok) throw new Error(`Answer save failed: ${response.status}`)

                if (this.pendingAnswers.get(stepKey) === formData) {
                    // Keep the latest snapshots in answer order, including branch changes.
        this.pendingAnswers.delete(stepKey)
                }
            } finally {
                clearTimeout(timeout)
            }
        }).catch(error => {
            this.answerSaveFailed = true
            console.error(`Error submitting form for step ${stepKey}:`, error)
        })

        return this.submissionQueue
    }

    async saveProfileAnswers(retry = false) {
        if (this.profileSaveAttempt) return

        const attempt = {}
        this.profileSaveAttempt = attempt
        this.flowTarget.inert = true
        this.profileSaveStatusTarget.classList.remove("hidden")
        this.profileSaveMessageTarget.textContent = "Saving your answers…"
        this.profileSaveRetryTarget.classList.add("hidden")

        if (retry) {
            this.answerSaveFailed = false
            for (const [stepKey, formData] of [...this.pendingAnswers]) {
                this.queueAnswer(stepKey, formData)
            }
        }

        await this.submissionQueue
        if (this.profileSaveAttempt !== attempt || this.disconnected) return

        this.profileSaveAttempt = null
        this.profileSaveRetryTarget.dispatchEvent(new CustomEvent("loading-button:stop"))
        if (this.pendingAnswers.size > 0) {
            this.profileSaveMessageTarget.textContent = "We couldn’t save all your answers. Check your connection and try again."
            this.profileSaveRetryTarget.classList.remove("hidden")
            return
        }

        this.showCurrentStep()
    }

    retryProfileSave() {
        return this.saveProfileAnswers(true)
    }

    // Handle description toggling for radio buttons
    toggleDescription(event) {
        try {
            if (!event?.target) return;

            const radio = event.target;
            const step = radio.closest('[data-onboarding-target="step"]');
            if (!step) return;

            // Hide all descriptions first
            step.querySelectorAll('.description-toggle').forEach(desc => {
                desc.classList.add('hidden');
            });

            // Show the selected description
            const selectedDesc = step.querySelector(`[data-show-when-selected="${radio.id}"]`);
            if (selectedDesc) {
                selectedDesc.classList.remove('hidden');
            }
        } catch (error) {
            console.error("Error toggling description:", error);
        }
    }

    // Method to handle animations-complete event
    handleAnimationsComplete(event) {
        try {
            // Get the current step key
            const currentStep = this.stepTargets[this.currentValue];
            const currentKey = currentStep?.dataset.stepKey;

            // Check if this is an animation-controlled step
            if (currentStep && this.animationControlledSteps.includes(currentKey)) {
                this.formCompletedValue = true;
                this.updateContinueButton();
            }
        } catch (error) {
            console.error("Error handling animations complete:", error);
        }
    }

    // Check if we're in a native app
    isNativeApp() {
        return document.documentElement.hasAttribute('data-turbo-native-app');
    }

    shouldPresentSignupPaywallBeforeStep(stepKey) {
        return this.isNativeApp() && stepKey === "register";
    }

    presentSignupPaywall() {
        const url = "/premium?placement=onboarding_signup&source=onboarding_signup";
        // placement records where the paywall was shown, matching the URL's
        // placement param. The variant is already on the field test membership,
        // so encoding it here would only make the property unusable for the
        // cross-surface placement breakdowns that read it.
        this.trackPaywallPresented("onboarding_signup")

        if (window.Turbo?.visit) {
            window.Turbo.visit(url)
        } else {
            window.location.href = url
        }
    }

    // The end-of-onboarding paywall is reached by following the continue
    // button's href, so unlike the signup paywall it has no call site to
    // instrument. Recording it here keeps the paywall_presented goal covering
    // every onboarding paywall entry instead of the signup one alone. The
    // handler never calls preventDefault, so the link navigates as before.
    handleContinueButtonClick(event) {
        const href = event.currentTarget?.getAttribute("href") || ""
        if (!href.startsWith("/premium")) return

        this.trackPaywallPresented("onboarding_end")
    }

    trackPaywallPresented(placement) {
        if (this.trackedPaywalls[placement]) return

        this.trackedPaywalls[placement] = true
        this.postClientEvent({ event: 'paywall_presented', placement: placement })
    }

    // The guest welcome's sign-in affordance is a plain link, so the tap is
    // recorded before the navigation commits (keepalive keeps the request
    // alive across it). Tracked in every variant: it measures how many people
    // arriving at onboarding already have an account.
    trackGuestWelcomeLogin() {
        if (this.trackedGuestWelcomeLogin) return

        this.trackedGuestWelcomeLogin = true
        this.postClientEvent({ event: 'native_guest_welcome_login_tapped' })
    }

    // Records a client-side onboarding event. Never calls preventDefault, so a
    // link or button that triggers one still behaves as it did.
    postClientEvent(body) {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        if (!csrfToken) return

        fetch('/onboarding/analytics', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
            body: JSON.stringify(body),
            credentials: 'same-origin',
            keepalive: true
        }).catch(error => console.error('Error tracking onboarding event:', error))
    }

    // Where to send the user when they finish onboarding. Premium users skip
    // the end-of-onboarding paywall entirely: on native we refresh the
    // underlying view (closing the modal and reloading home so the now-complete
    // profile is reflected), on web we drop them on premiumExitPath. Free users
    // still see the existing paywall pitch.
    onboardingExitHref() {
        if (this.userPremiumValue) {
            return this.isNativeApp() ? "/refresh_historical_location" : this.premiumExitPathValue;
        }
        return "/premium?placement=onboarding_end&source=onboarding_end";
    }

    triggerNativeReviewPromptOnStepEntry(step) {
        if (!this.shouldTriggerNativeReviewPrompt(step)) return

        this.nativeReviewPromptShownValue = true
        this.nativeReviewDelayActive = true

        if (this.nativeReviewContinueTimer) clearTimeout(this.nativeReviewContinueTimer)

        this.nativeReviewContinueTimer = setTimeout(() => {
            this.nativeReviewDelayActive = false
            this.nativeReviewContinueTimer = null
            this.updateContinueButton()
        }, this.NATIVE_REVIEW_CONTINUE_DELAY_MS)

        try {
            window.triggerNativeReviewPrompt?.()
        } catch (error) {
            console.error("Error triggering native review prompt:", error)
        }
    }

    shouldTriggerNativeReviewPrompt(step) {
        if (step?.dataset.nativeReviewPrompt !== "true" || this.nativeReviewPromptShownValue) return false

        const nativePlatform = document.documentElement.dataset.turboNativeApp

        return nativePlatform === "android" ||
            (nativePlatform === "ios" && this.routineHasProductsValue)
    }

    // Track onboarding step views through our onboarding analytics endpoint
    trackStepView(stepKey) {
        try {
            // Don't track if already tracked or stepKey is missing
            if (!stepKey || this.trackedSteps[stepKey]) return;

            // The projection screen's completion event carries gap + score
            // properties and is emitted server-side when its frame renders, so
            // the generic client ping is skipped to avoid a duplicate.
            if (stepKey === 'routine_projection') return;

            // Get the CSRF token from the meta tag
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
            if (!csrfToken) {
                console.error('CSRF token not found');
                return;
            }

            // Mark this step as tracked
            this.trackedSteps[stepKey] = true;

            // Call the analytics endpoint. keepalive matches postClientEvent:
            // the ping fires as the screen appears, so a fast tap through the
            // step, a reload, or the native app swapping the web view can tear
            // the page down before a plain fetch leaves the browser, and the
            // step then reads as a drop-off nobody actually made.
            fetch('/onboarding/analytics', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                body: JSON.stringify({ step_key: stepKey }),
                credentials: 'same-origin',
                keepalive: true
            }).catch(error => {
                console.error('Error tracking onboarding step:', error);
            });
        } catch (error) {
            console.error('Error in trackStepView:', error);
        }
    }

    // Method to handle product search tapped event
    handleProductSearchTapped(event) {
        try {
            this.productSearchTappedValue = true
            // Enable Continue as soon as the search is opened, in every
            // variant. transition() and initializeSteps() already treat
            // productSearchTappedValue as completion for this step, and Skip
            // is unavailable here, so gating this call leaves users who close
            // the search without adding a product with no way forward.
            this.updateFormCompleted()
        } catch (error) {
            console.error("Error handling product search tapped:", error)
        }
    }

    handleProductAdded() {
        try {
            this.productSearchTappedValue = true
            this.routineHasProductsValue = true
            this.afterProfileGenerationValue = "routine_information"
            this.nativeReviewPromptContentTargets.forEach(element => element.classList.remove("hidden"))
            this.updateFormCompleted()
        } catch (error) {
            console.error("Error handling product added:", error)
        }
    }

    // Decide whether to advance into referral_source_detail or skip past the
    // referral attribution branch entirely based on the source they picked.
    navigateFromReferralSource(event) {
        if (event) event.preventDefault()

        try {
            if (this.referralDetailTriggerSourcesValue.includes(this.referralSourceValue)) {
                this.advanceToStep("referral_source_detail")
            } else {
                this.advanceToStep(this.referralNextStepValue)
            }
        } catch (error) {
            console.error("Error navigating from referral source:", error)
        }
    }

    // Decide whether to advance into influencer_search or skip past it based
    // on whether the user said an influencer brought them in.
    navigateFromReferralSourceDetail(event) {
        if (event) event.preventDefault()

        try {
            if (this.referralSourceDetailValue === "influencer") {
                this.advanceToStep("influencer_search")
            } else {
                this.advanceToStep(this.referralNextStepValue)
            }
        } catch (error) {
            console.error("Error navigating from referral source detail:", error)
        }
    }

    syncReferralSourceDetailUi() {
        const step = this.stepTargets.find(s => s.dataset.stepKey === "referral_source_detail")
        if (!step) return

        step.querySelectorAll("[data-platform-variant]").forEach(el => {
            if (el.dataset.platformVariant === this.referralSourceValue) {
                el.classList.remove("hidden")
            } else {
                el.classList.add("hidden")
            }
        })
    }

    // From a step AFTER the referral attribution branch, walk back to the last
    // step the user actually answered.
    navigateBackFromAfterReferralBranch() {
        if (this.referralSourceDetailValue === "influencer") {
            this.advanceToStep("influencer_search")
        } else if (this.referralDetailTriggerSourcesValue.includes(this.referralSourceValue)) {
            this.advanceToStep("referral_source_detail")
        } else {
            this.advanceToStep("where_did_you_hear_about_us")
        }
    }

    hasReferralAttributionSteps() {
        return this.stepTargets.some(step => step.dataset.stepKey === "where_did_you_hear_about_us")
    }

    advanceToStep(stepKey) {
        const targetIndex = this.stepTargets.findIndex(step => step.dataset.stepKey === stepKey)

        if (targetIndex !== -1) {
            this.currentValue = targetIndex
            this.transition()
        } else if (this.currentValue < this.stepTargets.length - 1) {
            this.currentValue++
            this.transition()
        }
    }

    // The routine_information empty state offers a way back to the product
    // step, since the back arrow is disabled there (its previous step is an
    // animation in onboarding_4).
    navigateToAddProducts(event) {
        if (event) event.preventDefault()

        const targetIndex = this.stepTargets.findIndex(step => step.dataset.stepKey === "routine_add_products")
        if (targetIndex === -1) return

        this.routineInsightsStale = true
        this.currentValue = targetIndex
        this.transition()
    }

    // Special case for routine_step_1 - check routine_status and route accordingly
    navigateFromRoutineStep1(event) {
        if (event) event.preventDefault()

        try {
            // Get the selected routine_status value
            const routineStatus = document.querySelector('input[name="onboarding_step[data][routine_status]"]:checked')?.value;

            if (routineStatus === "no_routine") {
                // Navigate to no_routine_affirmation step
                const noRoutineAffirmationIndex = this.stepTargets.findIndex(step =>
                    step.dataset.stepKey === "no_routine_affirmation"
                )
                if (noRoutineAffirmationIndex !== -1) {
                    this.currentValue = noRoutineAffirmationIndex
                    this.transition()
                }
            } else {
                // For "have_routine_improve" and "have_products_no_routine", go to normal next step (routine_add_products)
                if (this.currentValue < this.stepTargets.length - 1) {
                    const routineAddProductsIndex = this.stepTargets.findIndex(step =>
                        step.dataset.stepKey === "routine_add_products"
                    )

                    this.currentValue = routineAddProductsIndex
                    this.transition()
                }
            }
        } catch (error) {
            console.error("Error navigating from routine_step_1:", error)
        }
    }
}
